import { ChartData, ChartDataset, ChartConfig } from '@components/svg-chart';
import { PointDataResult } from './chart-data-load.manager';

/**
 * Default colors for chart lines
 */
const CHART_COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8',
  '#F7DC6F', '#BB8FCE', '#85C1E9', '#F8B88B', '#FAD7A0'
];

/**
 * Legend item for display
 */
export interface LegendItem {
  name: string;
  color: string;
  minYear: number;
  maxYear: number;
}

/**
 * Manager for building and processing chart data
 */
export class ChartBuilderManager {
  // Store color assignments for results with identical data
  private colorAssignments: Map<number, string> = new Map();

  /**
   * Build ChartData from point data results
   * Points with identical data values get the same color
   */
  buildChartData(labels: string[], results: PointDataResult[]): ChartData {
    // Calculate color assignments based on data similarity
    this.assignColorsBasedOnData(results);

    const datasets: ChartDataset[] = results.map((result, index) => ({
      label: result.point.name,
      data: result.data,
      borderColor: this.colorAssignments.get(index) || this.getColor(index),
      borderWidth: 2,
      backgroundColor: 'transparent'
    }));

    return {
      labels,
      datasets
    };
  }

  /**
   * Assign colors to results - identical data gets the same color
   */
  private assignColorsBasedOnData(results: PointDataResult[]): void {
    this.colorAssignments.clear();

    // Group results by their data signature (stringified data array)
    const dataGroups = new Map<string, number[]>(); // signature -> indices

    results.forEach((result, index) => {
      const signature = this.getDataSignature(result.data);
      const existingGroup = dataGroups.get(signature);

      if (existingGroup) {
        existingGroup.push(index);
      } else {
        dataGroups.set(signature, [index]);
      }
    });

    // Assign colors: first index in each group gets a unique color,
    // other indices in the same group get the same color
    let colorIndex = 0;
    dataGroups.forEach((indices) => {
      const color = this.getColor(colorIndex);
      indices.forEach(idx => {
        this.colorAssignments.set(idx, color);
      });
      colorIndex++;
    });
  }

  /**
   * Create a signature string for data array to compare equality
   */
  private getDataSignature(data: (number | null)[]): string {
    // Round to 2 decimal places to handle floating point comparison
    return data.map(v => v !== null ? Math.round(v * 100) / 100 : 'null').join(',');
  }

  /**
   * Build chart configuration
   */
  buildChartConfig(yAxisUnit: string = ''): ChartConfig {
    return {
      showGridLines: true,
      yAxisUnit,
      viewBox: { width: 1000, height: 450 },
      padding: { top: 50, right: 50, bottom: 100, left: 50 },
      chartHeight: 350,
      gridLineCount: 13,
      lineStrokeWidth: 2
    };
  }

  /**
   * Build legend items from results
   * Uses the same color assignments as buildChartData
   */
  buildLegendItems(results: PointDataResult[]): LegendItem[] {
    return results
      .filter(r => r.success)
      .map((result, index) => ({
        name: result.point.name,
        color: this.colorAssignments.get(index) || this.getColor(index),
        minYear: result.minYear,
        maxYear: result.maxYear
      }));
  }

  /**
   * Get color for a dataset by index
   */
  getColor(index: number): string {
    return CHART_COLORS[index % CHART_COLORS.length];
  }

  /**
   * Format legend label based on year range
   */
  formatLegendLabel(name: string, minYear: number, maxYear: number): string {
    if (minYear === 0 && maxYear === 0) {
      return name;
    }
    if (minYear === maxYear) {
      return `${name} - ${minYear}`;
    }
    return `${name} (${minYear} - ${maxYear})`;
  }

  /**
   * Clear chart data
   */
  createEmptyChartData(): ChartData {
    return {
      labels: [],
      datasets: []
    };
  }

}
